import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { favoritesService } from '../../services/favorites';
import { GoodThing } from '../../types/good-things';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';

interface FavoriteButtonProps {
  goodThing: GoodThing;
  className?: string;
  onUnfavorite?: () => void;
}

export const FavoriteButton: React.FC<FavoriteButtonProps> = ({ goodThing, className, onUnfavorite }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { userEmail } = useAuth();

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!userEmail) return;
      try {
        const status = await favoritesService.isFavorite(userEmail, goodThing.id);
        setIsFavorite(status);
      } catch (error) {
        console.error('Error checking favorite status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkFavoriteStatus();
  }, [goodThing.id, userEmail]);

  const toggleFavorite = async () => {
    if (!userEmail || isLoading) return;

    try {
      setIsLoading(true);
      if (isFavorite) {
        await favoritesService.removeFromFavorites(userEmail, goodThing.id);
        if (onUnfavorite) {
          onUnfavorite();
        }
      } else {
        await favoritesService.addToFavorites(userEmail, goodThing);
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={toggleFavorite}
      disabled={isLoading || !userEmail}
      className={cn(
        "text-gray-400 hover:text-pink-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        isFavorite && "text-pink-500",
        className
      )}
      aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
    >
      <Heart className={`h-5 w-5 ${isFavorite ? 'fill-pink-500' : ''}`} />
    </button>
  );
};
